import { subject } from "@casl/ability";
import {
  FirestoreDataConverter,
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where
} from "firebase/firestore";
import { firestore } from "@/base";
import { getCurrentUser } from "@/services/auth";
import { getColaboradoresDocumentReference } from "../colaboradores/firestore";
import { getEmpresaDocumentReference } from "../empresas/firestore";
import { getRegulamentoIdeiasDocumentReference } from "../regulamento-ideias/firestore";

export type THistoricoPontosPerColaboradorForm = TFormWithTransformations<
  Omit<THistoricoPontosPerColaboradorBonificacao, "regulamentoDesafios" | "comiteInovacaoReference">,
  "colaboradorReference" | "regulamentoIdeias"
>;

type THistoricoPontosPerColaboradorDocument = TFirestoreDocument<
  THistoricoPontosPerColaboradorBonificacao,
  "colaboradorReference" | "comiteInovacaoReference" | "regulamentoDesafios" | "regulamentoIdeias"
>;

export const HISTORICO_PONTOS_PER_COLABORADOR_KEY = "historicoPontosPerColaborador";

const historicoPontosPerColaboradorConverter: FirestoreDataConverter<THistoricoPontosPerColaborador> = {
  toFirestore(data) {
    delete data.id;
    delete data.refPath;
    return data;
  },
  fromFirestore(snap) {
    const {
      createdAt,
      updatedAt,
      colaboradorReference,
      comiteInovacaoReference,
      regulamentoDesafios,
      regulamentoIdeias,
      ...document
    } = snap.data() as THistoricoPontosPerColaboradorDocument;

    const data: THistoricoPontosPerColaborador = {
      ...document,
      id: snap.id,
      createdAt: createdAt.toDate(),
      refPath: snap.ref.path,
      colaboradorReference: colaboradorReference.path,
      comiteInovacaoReference: comiteInovacaoReference?.path || null,
      regulamentoDesafios: regulamentoDesafios?.path || null,
      regulamentoIdeias: regulamentoIdeias?.path || null
    };

    if (updatedAt) {
      data.updatedAt = updatedAt.toDate();
    }
    return subject("THistoricoPontosPerColaborador", data);
  }
};

/**
 * Get historicoPontosPerColaborador collection reference
 * @param empresaId - Empresa id where the historicoPontosPerColaborador is located
 * @returns - historicoPontosPerColaborador collection reference
 */
function getHistoricoPontosPerColaboradorCollectionReference(empresaId: TEmpresa["id"]) {
  const empresaDocument = getEmpresaDocumentReference(empresaId);
  return collection(empresaDocument, HISTORICO_PONTOS_PER_COLABORADOR_KEY);
}

/**
 * Get historicoPontosPerColaborador document reference
 * @param empresaId - Empresa id where the historicoPontosPerColaborador is located
 * @param historicoId - Historico id to get
 * @returns - historicoPontosPerColaborador document reference
 */
function getHistoricoPontosPerColaboradorDocumentReference(
  empresaId: TEmpresa["id"],
  historicoId: THistoricoPontosPerColaborador["id"]
) {
  const historicoPontosPerColaboradorCollectionReference =
    getHistoricoPontosPerColaboradorCollectionReference(empresaId);
  return doc(historicoPontosPerColaboradorCollectionReference, historicoId);
}

/**
 * Get historicoPontosPerColaborador document reference from path
 * @param historicoRefPath - Historico document path
 * @param historicoReferencePath
 * @returns - historicoPontosPerColaborador document reference
 */
function getHistoricoPontosPerColaboradorDocumentReferenceFromPath(historicoReferencePath: string) {
  return doc(firestore, historicoReferencePath);
}

/**
 * Generate query to get historicoPontosPerColaborador  for colaborador in a specific period
 * @param empresaId - Empresa id where the historicoPontosPerColaborador is located
 * @param colaboradorId - Colaborador id to get historicoPontosPerColaborador for
 * @param regulamento - regulamento
 * @returns - historicoPontosPerColaborador query
 */
function generateHistoricoPontosPerColaboradorQuery(
  empresaId: TEmpresa["id"],
  colaboradorId: TColaborador["id"],
  regulamento: TRegulamentoIdeias
) {
  const historicoPontosPerColaboradorCollectionReference =
    getHistoricoPontosPerColaboradorCollectionReference(empresaId);
  const colaboradorReference = getColaboradoresDocumentReference(empresaId, colaboradorId);
  const regulamentoReference = getRegulamentoIdeiasDocumentReference(empresaId, regulamento.id);
  return query(
    historicoPontosPerColaboradorCollectionReference,
    where("colaboradorReference", "==", colaboradorReference),
    where("regulamentoIdeias", "==", regulamentoReference),
    orderBy("createdAt", "desc")
  );
}

/**
 * Get historicoPontosPerColaborador for colaborador in a specific period
 * @param empresaId - Empresa id where the historicoPontosPerColaborador is located
 * @param colaboradorId - Colaborador id to get historicoPontosPerColaborador for
 * @param regulamento - regulamento
 * @returns - historicoPontosPerColaborador
 */
export function getHistoricoPontosPerColaborador(
  empresaId: TEmpresa["id"],
  colaboradorId: TColaborador["id"],
  regulamento: TRegulamentoIdeias
) {
  const historicoPontosPerColaboradorQuery = generateHistoricoPontosPerColaboradorQuery(
    empresaId,
    colaboradorId,
    regulamento
  ).withConverter(historicoPontosPerColaboradorConverter);
  return getDocs(historicoPontosPerColaboradorQuery);
}

/**
 * Add a historicoPontosPerColaborador to a empresa
 * @param empresaId - Empresa id where the historicoPontosPerColaborador is located
 * @param historicoPontosPerColaborador - historicoPontosPerColaborador to add
 * @returns - The added historicoPontosPerColaborador
 */
export function addHistoricoPontosPerColaborador(
  empresaId: TEmpresa["id"],
  historicoPontosPerColaborador: THistoricoPontosPerColaboradorForm
) {
  const historicoPontosPerColaboradorCollectionReference =
    getHistoricoPontosPerColaboradorCollectionReference(empresaId);
  return addDoc(historicoPontosPerColaboradorCollectionReference, historicoPontosPerColaborador);
}

/**
 * Update a historicoPontosPerColaborador in a empresa
 * @param empresaId - Empresa id where the historicoPontosPerColaborador is located
 * @param historicoId - historicoPontosPerColaborador id to update
 * @param historicoPontosPerColaborador - historicoPontosPerColaborador to update
 * @param historicoRefPath
 * @param historicoReferencePath
 * @returns - The updated historicoPontosPerColaborador
 */
export function updateHistoricoPontosPerColaborador(
  empresaId: TEmpresa["id"],
  historicoId: THistoricoPontosPerColaborador["id"],
  historicoPontosPerColaborador: Partial<THistoricoPontosPerColaboradorForm>,
  historicoReferencePath?: THistoricoPontosPerColaborador["refPath"]
) {
  const historicoPontosPerColaboradorDocumentReference = historicoReferencePath
    ? getHistoricoPontosPerColaboradorDocumentReferenceFromPath(historicoReferencePath)
    : getHistoricoPontosPerColaboradorDocumentReference(empresaId, historicoId);
  const currentUserId = getCurrentUser()?.uid;
  return updateDoc(historicoPontosPerColaboradorDocumentReference, {
    ...historicoPontosPerColaborador,
    updatedAt: serverTimestamp(),
    updatedBy: currentUserId
  });
}

/**
 * Delete a historicoPontosPerColaborador in a empresa
 * @param empresaId - Empresa id where the historicoPontosPerColaborador is located
 * @param historicoId - historicoPontosPerColaborador id to remove
 * @param historicoRefPath
 * @param historicoReferencePath
 * @returns - The deleted historicoPontosPerColaborador
 */
export function deleteHistoricoPontosPerColaborador(
  empresaId: TEmpresa["id"],
  historicoId: THistoricoPontosPerColaborador["id"],
  historicoReferencePath?: THistoricoPontosPerColaborador["refPath"]
) {
  const historicoPontosPerColaboradorDocumentReference = historicoReferencePath
    ? getHistoricoPontosPerColaboradorDocumentReferenceFromPath(historicoReferencePath)
    : getHistoricoPontosPerColaboradorDocumentReference(empresaId, historicoId);
  return deleteDoc(historicoPontosPerColaboradorDocumentReference);
}
